import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { StatsCard } from '../components/ui/stats-card';
import { Skeleton } from '../components/ui/skeleton';
import { ResponsiveTransporterList } from '../components/ui/responsive-transporter-list';
import { Pagination } from '../components/ui/pagination';
import { StatusBadge } from '../components/ui/status-badge';
import {
  Search,
  Plus,
  Filter,
  Download,
  X,
  MapPin,
  User,
  Mail,
  Phone,
  Truck,
  Loader2,
  Users,
  UserCheck,
  CheckCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  ShoppingCart,
  Calendar,
  CreditCard,
  FileText,
  Settings
} from 'lucide-react';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { EnhancedTransporter, AdminTransporterAPI, TransporterCreateRequest, TransporterStats, OrderAPI } from '../lib/api';
import { toast } from '../components/ui/use-toast';
import OrderDetailModal from '../components/orders/OrderDetailModal';
import TransporterCreateModal from '../components/transporters/TransporterCreateModal';
import TransporterEditModal from '../components/transporters/TransporterEditModal';
import VehicleCreateModal from '../components/transporters/VehicleCreateModal';
import VehicleEditModal from '../components/transporters/VehicleEditModal';
import DocumentUploadModal from '../components/transporters/DocumentUploadModal';

export default function TransportersPage() {
  const [transporters, setTransporters] = useState<EnhancedTransporter[]>([]);
  const [selectedTransporter, setSelectedTransporter] = useState<EnhancedTransporter | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [isToggling, setIsToggling] = useState<string | null>(null);
  const [editingTransporter, setEditingTransporter] = useState<EnhancedTransporter | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTransporters, setTotalTransporters] = useState(0);
  const [transporterStats, setTransporterStats] = useState<TransporterStats | null>(null);
  const [verificationStatusFilter, setVerificationStatusFilter] = useState<string>('all');
  const [activeStatusFilter, setActiveStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const pageSize = 10;

  // Order-related states
  const [transporterOrders, setTransporterOrders] = useState<any[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [orderCurrentPage, setOrderCurrentPage] = useState(1);
  const [orderTotalPages, setOrderTotalPages] = useState(1);
  const [orderTotalCount, setOrderTotalCount] = useState(0);
  const orderPageSize = 10;

  // Vehicle and document related states
  const [isAddVehicleModalOpen, setIsAddVehicleModalOpen] = useState(false);
  const [isUploadDocumentModalOpen, setIsUploadDocumentModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch transporters on component mount or when search/page/filters change
  useEffect(() => {
    fetchTransporters();
  }, [currentPage, debouncedSearchQuery, verificationStatusFilter, activeStatusFilter, sortBy, sortOrder]);

  // Fetch transporter statistics on component mount
  useEffect(() => {
    fetchTransporterStatistics();
  }, []);

  const fetchTransporterStatistics = async () => {
    try {
      const response = await AdminTransporterAPI.getTransporterStatistics({
        period: '30d'
      });

      if (response.success) {
        setTransporterStats(response.data);
      } else {
        console.error('Failed to fetch transporter statistics:', response.message);
      }
    } catch (error) {
      console.error('Error fetching transporter statistics:', error);
    }
  };

  const fetchTransporters = async () => {
    try {
      setIsLoading(true);
      const response = await AdminTransporterAPI.getAllTransporters({
        search: debouncedSearchQuery || undefined,
        verificationStatus: verificationStatusFilter !== 'all' ? verificationStatusFilter : undefined,
        isActive: activeStatusFilter !== 'all' ? activeStatusFilter === 'active' : undefined,
        page: currentPage,
        limit: pageSize,
        sortBy: sortBy,
        sortOrder: sortOrder,
        includeStats: true
      });


      if (response.success) {
        const transportersList = response.data.transporters;
        const paginationData = response.data.pagination;

        if (paginationData) {
          setTotalPages(paginationData.pages);
          setTotalTransporters(paginationData.total);
        }

        if (Array.isArray(transportersList)) {
          setTransporters(transportersList);
        } else {
          console.warn('Expected transporters array but got:', transportersList);
          setTransporters([]);
        }
      } else {
        toast({
          title: "Error",
          description: "Failed to fetch transporters",
          variant: "destructive"
        });
        setTransporters([]);
      }
    } catch (error) {
      console.error("Error fetching transporters:", error);
      toast({
        title: "Error",
        description: "Failed to fetch transporters. Please try again.",
        variant: "destructive"
      });
      setTransporters([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle transporter deletion
  const handleDeleteTransporter = async (transporterId: string) => {
    if (!window.confirm("Are you sure you want to delete this transporter? This action cannot be undone.")) {
      return;
    }

    try {
      setIsDeleting(transporterId);
      const response = await AdminTransporterAPI.deleteTransporter(transporterId, {
        permanent: false,
        reason: "Deleted by admin"
      });

      if (response.success) {
        toast({
          title: "Success",
          description: "Transporter deleted successfully"
        });

        setTransporters(prev => prev.filter(t => t._id !== transporterId));
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to delete transporter",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error deleting transporter:", error);
      toast({
        title: "Error",
        description: "Failed to delete transporter. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(null);
    }
  };

  // Handle toggling transporter active status
  const handleToggleActive = async (transporterId: string) => {
    try {
      setIsToggling(transporterId);
      const transporter = transporters.find(t => t._id === transporterId);
      if (!transporter) return;

      const response = await AdminTransporterAPI.updateTransporter(transporterId, {
        isActive: !transporter.isActive,
        adminNotes: `Status changed to ${!transporter.isActive ? 'active' : 'inactive'} by admin`
      });

      if (response.success) {
        toast({
          title: "Success",
          description: `Transporter ${response.data.isActive ? 'activated' : 'deactivated'} successfully`
        });

        setTransporters(prev => prev.map(t => t._id === transporterId ? response.data : t));

        if (selectedTransporter && selectedTransporter._id === transporterId) {
          setSelectedTransporter(response.data);
        }
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to update transporter status",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error toggling transporter status:", error);
      toast({
        title: "Error",
        description: "Failed to update transporter status. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsToggling(null);
    }
  };

  // Handle viewing transporter details
  const handleViewTransporter = async (transporterId: string) => {
    try {
      setIsLoading(true);
      const response = await AdminTransporterAPI.getTransporterById(transporterId);

      if (response.success) {
        setSelectedTransporter(response.data);
        fetchTransporterOrders(transporterId);
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to fetch transporter details",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error fetching transporter details:", error);
      toast({
        title: "Error",
        description: "Failed to fetch transporter details. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle editing transporter
  const handleEditTransporter = (transporter: EnhancedTransporter) => {
    setEditingTransporter(transporter);
  };

  // Fetch transporter orders
  const fetchTransporterOrders = async (transporterId: string) => {
    try {
      setIsLoadingOrders(true);
      const response = await AdminTransporterAPI.getTransporterOrderHistory(transporterId, {
        page: orderCurrentPage,
        limit: orderPageSize,
        status: orderStatusFilter !== 'all' ? orderStatusFilter : undefined
      });

      if (response.success && response.data.orders) {
        setTransporterOrders(response.data.orders);
        setOrderTotalPages(response.data.pagination?.pages || 1);
        setOrderTotalCount(response.data.pagination?.total || 0);
      } else {
        setTransporterOrders([]);
        setOrderTotalPages(1);
        setOrderTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching transporter orders:", error);
      setTransporterOrders([]);
      setOrderTotalPages(1);
      setOrderTotalCount(0);
    } finally {
      setIsLoadingOrders(false);
    }
  };

  // Handle order status filter change
  const handleOrderStatusFilterChange = (status: string) => {
    setOrderStatusFilter(status);
    setOrderCurrentPage(1);
    if (selectedTransporter) {
      fetchTransporterOrders(selectedTransporter._id);
    }
  };

  // Handle order pagination
  const handleOrderPageChange = (newPage: number) => {
    setOrderCurrentPage(newPage);
    if (selectedTransporter) {
      fetchTransporterOrders(selectedTransporter._id);
    }
  };

  // Handle viewing order details
  const handleViewOrderDetails = async (orderId: string) => {
    try {
      const response = await OrderAPI.getOrder(orderId);

      if (response.success) {
        setSelectedOrder(response.data);
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to fetch order details",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
      toast({
        title: "Error",
        description: "Failed to fetch order details. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Format date helper
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format currency helper
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  // Get status badge variant
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'pending':
        return 'secondary';
      case 'processing':
        return 'default';
      case 'confirmed':
        return 'default';
      case 'shipped':
        return 'outline';
      case 'delivered':
        return 'default';
      case 'completed':
        return 'default';
      case 'cancelled':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  // Readable label: "in_transit" -> "In Transit", "mini_truck" -> "Mini Truck".
  const formatLabel = (value?: string) =>
    (value || '')
      .split('_')
      .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
      .join(' ');

  // Helper function to check if transporter is verified
  const isVerified = (transporter: EnhancedTransporter) => {
    return transporter.verificationStatus === 'verified';
  };

  // Handle verifying a transporter
  const handleVerifyTransporter = async (transporterId: string) => {
    try {
      setIsLoading(true);
      const response = await AdminTransporterAPI.updateTransporter(transporterId, {
        verificationStatus: 'verified',
        adminNotes: 'Verified by admin'
      });

      if (response.success) {
        toast({
          title: "Success",
          description: "Transporter verified successfully"
        });

        if (selectedTransporter && selectedTransporter._id === transporterId) {
          setSelectedTransporter(response.data);
        }
        setTransporters(prev => prev.map(t => t._id === transporterId ? response.data : t));
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to verify transporter",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error verifying transporter:", error);
      toast({
        title: "Error",
        description: "Failed to verify transporter. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle updating a transporter
  const handleUpdateTransporter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTransporter) return;

    try {
      setIsSubmitting(true);

      const updateData = {
        name: editingTransporter.name,
        mobile: editingTransporter.mobile,
        alternatePhone: editingTransporter.alternatePhone,
        address: {
          street: editingTransporter.address?.street || '',
          city: editingTransporter.address?.city || '',
          state: editingTransporter.address?.state || '',
          country: editingTransporter.address?.country || 'India',
          pincode: editingTransporter.address?.pincode || ''
        },
        adminNotes: editingTransporter.adminNotes
      };

      const response = await AdminTransporterAPI.updateTransporter(editingTransporter._id, updateData);

      if (response.success) {
        toast({
          title: "Success",
          description: "Transporter updated successfully"
        });

        if (selectedTransporter && selectedTransporter._id === editingTransporter._id) {
          setSelectedTransporter(response.data);
        }
        setTransporters(prev => prev.map(t => t._id === editingTransporter._id ? response.data : t));
        setEditingTransporter(null);
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to update transporter",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error updating transporter:", error);
      toast({
        title: "Error",
        description: "Failed to update transporter. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add a safeguard to ensure transporters is always an array
  useEffect(() => {
    if (!Array.isArray(transporters)) {
      console.error('Transporters state is not an array:', transporters);
      setTransporters([]);
    }
  }, [transporters]);
  // Render the transporters page
  return (
    <div className="flex flex-col h-full">
      {/* Main view when no transporter is selected */}
      {!selectedTransporter ? (
        <>
          {/* Fixed Header section - stays at top */}
          <div className="flex-shrink-0 space-y-4 sm:space-y-6 p-4 sm:p-6 bg-background border-b">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">Transporters</h1>
                <p className="text-muted-foreground text-sm sm:text-base">Manage your transportation network and drivers</p>
              </div>
              <Button onClick={() => setIsAddModalOpen(true)} className="w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                Add Transporter
              </Button>
            </div>

            {/* Stats cards */}
            {isLoading ? (
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="border rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <div className="space-y-2">
                        <Skeleton className="h-8 w-16" />
                        <Skeleton className="h-4 w-24" />
                      </div>
                      <Skeleton className="h-10 w-10 rounded-full" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                <StatsCard
                  title="Total Transporters"
                  value={transporterStats?.totalTransporters || totalTransporters}
                  icon={<Truck className="h-6 w-6" />}
                  iconClassName="bg-primary/10 text-primary"
                />
                <StatsCard
                  title="Active Transporters"
                  value={transporterStats?.activeTransporters || transporters.filter(t => t.isActive).length}
                  icon={<UserCheck className="h-6 w-6" />}
                  iconClassName="bg-green-100 text-green-600"
                />
                <StatsCard
                  title="Verified Transporters"
                  value={transporterStats?.verifiedTransporters || transporters.filter(t => t.verificationStatus === 'verified').length}
                  icon={<CheckCircle className="h-6 w-6" />}
                  iconClassName="bg-blue-100 text-blue-600"
                />
                <StatsCard
                  title="Pending Verification"
                  value={transporterStats?.pendingVerification || transporters.filter(t => t.verificationStatus === 'pending').length}
                  icon={<Clock className="h-6 w-6" />}
                  iconClassName="bg-yellow-100 text-yellow-600"
                />
              </div>
            )}

            {/* Search and filters */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder="Search transporters..."
                    className="pl-8"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm">
                    <Filter className="h-4 w-4 mr-2" />
                    Filters
                  </Button>
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                </div>
              </div>

              {/* Enhanced filters */}
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="verification-filter">Verification Status</Label>
                  <Select value={verificationStatusFilter} onValueChange={setVerificationStatusFilter}>
                    <SelectTrigger id="verification-filter">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="verified">Verified</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="active-filter">Active Status</Label>
                  <Select value={activeStatusFilter} onValueChange={setActiveStatusFilter}>
                    <SelectTrigger id="active-filter">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Transporters</SelectItem>
                      <SelectItem value="active">Active Only</SelectItem>
                      <SelectItem value="inactive">Inactive Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sort-by">Sort By</Label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger id="sort-by">
                      <SelectValue placeholder="Name" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name">Name</SelectItem>
                      <SelectItem value="createdAt">Registration Date</SelectItem>
                      <SelectItem value="verificationStatus">Verification Status</SelectItem>
                      <SelectItem value="rating">Rating</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sort-order">Sort Order</Label>
                  <Select value={sortOrder} onValueChange={(value: 'asc' | 'desc') => setSortOrder(value)}>
                    <SelectTrigger id="sort-order">
                      <SelectValue placeholder="Ascending" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asc">Ascending</SelectItem>
                      <SelectItem value="desc">Descending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          {/* Transporter List Container */}
          <div className="px-6 pb-6">
            <div>
              <ResponsiveTransporterList
                transporters={transporters}
                isLoading={isLoading}
                onView={handleViewTransporter}
                onEdit={handleEditTransporter}
                onDelete={handleDeleteTransporter}
                onToggleActive={handleToggleActive}
                onVerify={handleVerifyTransporter}
                isDeleting={isDeleting}
                isToggling={isToggling}
              />
            </div>

            {/* Fixed Pagination at bottom */}
            {!isLoading && transporters.length > 0 && (
              <div className="flex-shrink-0 p-4 border-t bg-background">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  summary={`Showing ${transporters.length} of ${totalTransporters} transporters`}
                />
              </div>
            )}
          </div>
        </>
      ) : (
        /* Detailed Transporter View */
        <div className="flex flex-col h-full">
          {/* Header with back button */}
          <div className="flex-shrink-0 p-6 border-b bg-background">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedTransporter(null)}
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back to Transporters
                </Button>
                <div>
                  <h1 className="text-2xl font-bold">{selectedTransporter.name}</h1>
                  <p className="text-muted-foreground">{selectedTransporter.email}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <StatusBadge status={selectedTransporter.isActive ? 'active' : 'inactive'} />
                <StatusBadge status={selectedTransporter.verificationStatus} />
              </div>
            </div>
          </div>

          {/* Transporter Details with Tabs */}
          <div className="flex-1 overflow-hidden">
            <Tabs defaultValue="overview" className="h-full flex flex-col">
              <div className="flex-shrink-0 px-6 pt-4">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="orders">Orders ({orderTotalCount})</TabsTrigger>
                  <TabsTrigger value="vehicles">Vehicles</TabsTrigger>
                  <TabsTrigger value="documents">Documents</TabsTrigger>
                </TabsList>
              </div>

              <div className="flex-1 overflow-auto">
                <TabsContent value="overview" className="space-y-6 p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Basic Information */}
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-semibold mb-4 flex items-center">
                        <User className="h-5 w-5 mr-2" />
                        Basic Information
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <div className="text-sm font-medium text-muted-foreground">Name</div>
                          <div className="text-sm">{selectedTransporter.name}</div>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-muted-foreground">Email</div>
                          <div className="text-sm">{selectedTransporter.email}</div>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-muted-foreground">Mobile</div>
                          <div className="text-sm">{selectedTransporter.mobile}</div>
                        </div>
                        {selectedTransporter.alternatePhone && (
                          <div>
                            <div className="text-sm font-medium text-muted-foreground">Alternate Phone</div>
                            <div className="text-sm">{selectedTransporter.alternatePhone}</div>
                          </div>
                        )}
                        <div>
                          <div className="text-sm font-medium text-muted-foreground">Registration Date</div>
                          <div className="text-sm">{formatDate(selectedTransporter.createdAt)}</div>
                        </div>
                      </div>
                    </div>

                    {/* Address Information */}
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-semibold mb-4 flex items-center">
                        <MapPin className="h-5 w-5 mr-2" />
                        Address
                      </h3>
                      <div className="space-y-2">
                        <div className="text-sm">{selectedTransporter.address.street}</div>
                        <div className="text-sm">{selectedTransporter.address.city}, {selectedTransporter.address.state}</div>
                        <div className="text-sm">{selectedTransporter.address.pincode}</div>
                        <div className="text-sm">{selectedTransporter.address.country}</div>
                      </div>
                    </div>

                    {/* Performance Metrics */}
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-semibold mb-4 flex items-center">
                        <Truck className="h-5 w-5 mr-2" />
                        Performance
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <div className="text-sm font-medium text-muted-foreground">Rating</div>
                          <div className="text-2xl font-bold">{selectedTransporter.rating || 'N/A'}</div>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-muted-foreground">Completed Trips</div>
                          <div className="text-2xl font-bold">{selectedTransporter.completedTrips || 0}</div>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-muted-foreground">Total Vehicles</div>
                          <div className="text-2xl font-bold">{selectedTransporter.vehicles?.length || 0}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* License Information */}
                  {selectedTransporter.drivingLicenseNumber && (
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-semibold mb-4 flex items-center">
                        <FileText className="h-5 w-5 mr-2" />
                        License Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="text-sm font-medium text-muted-foreground">License Number</div>
                          <div className="text-sm">{selectedTransporter.drivingLicenseNumber}</div>
                        </div>
                        {selectedTransporter.drivingLicenseExpiry && (
                          <div>
                            <div className="text-sm font-medium text-muted-foreground">License Expiry</div>
                            <div className="text-sm">{formatDate(selectedTransporter.drivingLicenseExpiry)}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="orders" className="space-y-4">
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-xl font-semibold">Order History ({orderTotalCount})</h2>
                      <div className="flex space-x-2">
                        <Select value={orderStatusFilter} onValueChange={handleOrderStatusFilterChange}>
                          <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Filter by Status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Orders</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="processing">Processing</SelectItem>
                            <SelectItem value="confirmed">Confirmed</SelectItem>
                            <SelectItem value="shipped">Shipped</SelectItem>
                            <SelectItem value="delivered">Delivered</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="outline">Export</Button>
                      </div>
                    </div>
                    <div className="space-y-4">
                      {isLoadingOrders ? (
                        <div className="space-y-4">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <div key={i} className="border rounded-lg p-4">
                              <div className="flex justify-between items-center">
                                <div className="space-y-2">
                                  <Skeleton className="h-4 w-32" />
                                  <Skeleton className="h-3 w-24" />
                                </div>
                                <Skeleton className="h-6 w-20" />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : transporterOrders.length > 0 ? (
                        <>
                          {transporterOrders.map((order: any) => (
                            <div key={order._id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                              <div className="flex justify-between items-start">
                                <div className="space-y-2">
                                  <div className="flex items-center space-x-2">
                                    <span className="font-medium">Order #{order.orderNumber}</span>
                                    <Badge variant={getStatusBadgeVariant(order.status)}>
                                      {formatLabel(order.status)}
                                    </Badge>
                                  </div>
                                  <div className="text-sm text-muted-foreground">
                                    <div>Customer: {order.customer?.name || 'N/A'}</div>
                                    <div>Date: {formatDate(order.createdAt)}</div>
                                    <div>Amount: {formatCurrency(order.totalAmount || 0)}</div>
                                  </div>
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewOrderDetails(order._id)}
                                >
                                  View Details
                                </Button>
                              </div>
                            </div>
                          ))}

                          {/* Order Pagination */}
                          {orderTotalPages > 1 && (
                            <div className="flex justify-center items-center space-x-2 pt-4">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOrderPageChange(orderCurrentPage - 1)}
                                disabled={orderCurrentPage === 1}
                              >
                                <ChevronLeft className="h-4 w-4" />
                                Previous
                              </Button>

                              <span className="text-sm text-muted-foreground">
                                Page {orderCurrentPage} of {orderTotalPages}
                              </span>

                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOrderPageChange(orderCurrentPage + 1)}
                                disabled={orderCurrentPage === orderTotalPages}
                              >
                                Next
                                <ChevronRight className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <ShoppingCart className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
                          <p>No orders found for this transporter.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="vehicles" className="space-y-4">
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-xl font-semibold">Vehicles ({selectedTransporter.vehicles?.length || 0})</h2>
                      <Button onClick={() => setIsAddVehicleModalOpen(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Vehicle
                      </Button>
                    </div>
                    <div className="space-y-4">
                      {selectedTransporter.vehicles && selectedTransporter.vehicles.length > 0 ? (
                        selectedTransporter.vehicles.map((vehicle: any) => (
                          <div key={vehicle._id} className="border rounded-lg p-4">
                            <div className="flex justify-between items-start">
                              <div className="space-y-2">
                                <div className="flex items-center space-x-2">
                                  <Truck className="h-4 w-4" />
                                  <span className="font-medium">{vehicle.registrationNumber}</span>
                                  <Badge variant={vehicle.isActive ? "default" : "secondary"}>
                                    {vehicle.isActive ? 'Active' : 'Inactive'}
                                  </Badge>
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  <div>Type: {formatLabel(vehicle.type)}</div>
                                  <div>Capacity: {vehicle.capacity?.value} {vehicle.capacity?.unit}</div>
                                  <div>Insurance Expiry: {formatDate(vehicle.insuranceExpiryDate)}</div>
                                </div>
                              </div>
                              <div className="flex space-x-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setSelectedVehicle(vehicle)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setIsUploadDocumentModalOpen(true)}
                                >
                                  Documents
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <Truck className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
                          <p>No vehicles registered for this transporter.</p>
                          <Button className="mt-4" onClick={() => setIsAddVehicleModalOpen(true)}>
                            <Plus className="h-4 w-4 mr-2" />
                            Add First Vehicle
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="documents" className="space-y-4">
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-xl font-semibold">Documents ({selectedTransporter.documents?.length || 0})</h2>
                      <Button onClick={() => setIsUploadDocumentModalOpen(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Upload Document
                      </Button>
                    </div>
                    <div className="space-y-4">
                      {selectedTransporter.documents && selectedTransporter.documents.length > 0 ? (
                        selectedTransporter.documents.map((document: any) => (
                          <div key={document._id} className="border rounded-lg p-4">
                            <div className="flex justify-between items-start">
                              <div className="space-y-2">
                                <div className="flex items-center space-x-2">
                                  <FileText className="h-4 w-4" />
                                  <span className="font-medium">{document.name}</span>
                                  <Badge variant="outline">{formatLabel(document.type)}</Badge>
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  Uploaded: {formatDate(document.uploadedAt)}
                                </div>
                              </div>
                              <div className="flex space-x-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => window.open(document.url, '_blank')}
                                >
                                  View
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const link = document.createElement('a');
                                    link.href = document.url;
                                    link.download = document.name;
                                    link.click();
                                  }}
                                >
                                  Download
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600"
                                  onClick={async () => {
                                    if (window.confirm('Are you sure you want to delete this document?')) {
                                      try {
                                        const response = await AdminTransporterAPI.deleteTransporterDocument(selectedTransporter._id, document._id);
                                        if (response.success) {
                                          toast({
                                            title: "Success",
                                            description: "Document deleted successfully"
                                          });
                                          handleViewTransporter(selectedTransporter._id);
                                        } else {
                                          toast({
                                            title: "Error",
                                            description: response.message || "Failed to delete document",
                                            variant: "destructive"
                                          });
                                        }
                                      } catch (error) {
                                        toast({
                                          title: "Error",
                                          description: "Failed to delete document",
                                          variant: "destructive"
                                        });
                                      }
                                    }
                                  }}
                                >
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <FileText className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
                          <p>No documents uploaded for this transporter.</p>
                          <Button className="mt-4" onClick={() => setIsUploadDocumentModalOpen(true)}>
                            <Plus className="h-4 w-4 mr-2" />
                            Upload First Document
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </div>
      )}

      {/* Add Transporter Modal */}
      <TransporterCreateModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={() => {
          fetchTransporters();
          fetchTransporterStatistics();
        }}
      />

      {/* Edit Transporter Modal */}
      {editingTransporter && (
        <TransporterEditModal
          isOpen={!!editingTransporter}
          onClose={() => setEditingTransporter(null)}
          onSuccess={() => {
            fetchTransporters();
            fetchTransporterStatistics();
            if (selectedTransporter && selectedTransporter._id === editingTransporter._id) {
              handleViewTransporter(editingTransporter._id);
            }
          }}
          transporter={editingTransporter}
        />
      )}

      {/* Order Detail Modal */}
      <OrderDetailModal
        isOpen={!!selectedOrder}
        onClose={() => setSelectedOrder(null)}
        order={selectedOrder}
      />

      {/* Vehicle Create Modal */}
      {selectedTransporter && (
        <VehicleCreateModal
          isOpen={isAddVehicleModalOpen}
          onClose={() => setIsAddVehicleModalOpen(false)}
          onSuccess={() => {
            handleViewTransporter(selectedTransporter._id);
          }}
          transporterId={selectedTransporter._id}
        />
      )}

      {/* Vehicle Edit Modal */}
      {selectedTransporter && selectedVehicle && (
        <VehicleEditModal
          isOpen={!!selectedVehicle}
          onClose={() => setSelectedVehicle(null)}
          onSuccess={() => {
            handleViewTransporter(selectedTransporter._id);
            setSelectedVehicle(null);
          }}
          transporterId={selectedTransporter._id}
          vehicle={selectedVehicle}
        />
      )}

      {/* Document Upload Modal */}
      {selectedTransporter && (
        <DocumentUploadModal
          isOpen={isUploadDocumentModalOpen}
          onClose={() => setIsUploadDocumentModalOpen(false)}
          onSuccess={() => {
            handleViewTransporter(selectedTransporter._id);
          }}
          transporterId={selectedTransporter._id}
        />
      )}
    </div>
  );
}